import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { entryTypesService } from '../../services/entryTypes.service';
import { EntryTypeDto } from '../../types/entryTypes.types';

interface EntryTypeSelectorProps {
  value?: number | null;
  onChange: (entryTypeId: number | null, entryType: EntryTypeDto | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  onlyManualEntry?: boolean;
}

const EntryTypeSelector: React.FC<EntryTypeSelectorProps> = ({
  value,
  onChange,
  label,
  error = false,
  helperText,
  disabled = false,
  required = false,
  fullWidth = true,
  onlyManualEntry = true
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [entryTypes, setEntryTypes] = useState<EntryTypeDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadEntryTypes();
  }, [onlyManualEntry]);

  const loadEntryTypes = async () => {
    try {
      setLoading(true);
      const data = onlyManualEntry
        ? await entryTypesService.getManualEntryTypes()
        : await entryTypesService.getAll(false);
      setEntryTypes(data);
    } catch (err) {
      console.error('Failed to load entry types:', err);
      setEntryTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    if (newValue === '') {
      onChange(null, null);
    } else {
      const selectedType = entryTypes.find(et => et.entryTypeId === newValue);
      onChange(newValue, selectedType || null);
    }
  };

  return (
    <FormControl fullWidth={fullWidth} error={error} disabled={disabled} required={required}>
      <InputLabel>{label || (isRTL ? 'نوع القيد' : 'Entry Type')}</InputLabel>
      <Select
        value={value || ''}
        onChange={handleChange}
        label={label || (isRTL ? 'نوع القيد' : 'Entry Type')}
        endAdornment={loading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
      >
        {entryTypes.map((et) => (
          <MenuItem 
            key={et.entryTypeId} 
            value={et.entryTypeId}
            sx={{ 
              color: 'text.primary',
              '&:hover': { 
                backgroundColor: 'action.hover' 
              }
            }}
          >
            {et.prefix} - {isRTL ? et.entryTypeNameAr : et.entryTypeName}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default EntryTypeSelector;